import { useState, useCallback, useEffect } from "react";
import type { PaginationState } from "@tanstack/react-table";
import { useAllowedPageSizes } from "@/app/providers/useAllowedPageSizes";

export interface UseStaffPaginationOptions {
  initialPagination: PaginationState;
  onPaginationChange: (pagination: PaginationState) => void;
}

export interface UseStaffPaginationReturn {
  pagination: PaginationState;
  currentPage: number;
  pageSize: number;
  allowedPageSizes: readonly number[];
  setPagination: (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (size: number) => void;
}

export function useStaffPagination({
  initialPagination,
  onPaginationChange,
}: UseStaffPaginationOptions): UseStaffPaginationReturn {
  const [pagination, setPaginationState] = useState<PaginationState>(initialPagination);

  useEffect(() => {
    setPaginationState(initialPagination);
  }, [initialPagination]);

  const { data: allowedPageSizesData } = useAllowedPageSizes();
  const allowedPageSizes = allowedPageSizesData?.allowedPageSizes || [30, 60, 90];

  const currentPage = pagination.pageIndex + 1;
  const pageSize = pagination.pageSize;

  const setPagination = useCallback(
    (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => {
      const newPagination =
        typeof updater === "function" ? updater(pagination) : updater;
      setPaginationState(newPagination);
      onPaginationChange(newPagination);
    },
    [pagination, onPaginationChange]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      const newPagination = {
        pageIndex: 0,
        pageSize: newPageSize,
      };
      setPaginationState(newPagination);
      onPaginationChange(newPagination);
    },
    [onPaginationChange]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      const newPagination = {
        ...pagination,
        pageIndex: page - 1,
      };
      setPaginationState(newPagination);
      onPaginationChange(newPagination);
    },
    [pagination, onPaginationChange]
  );

  return {
    pagination,
    currentPage,
    pageSize,
    allowedPageSizes,
    setPagination,
    handlePageChange,
    handlePageSizeChange,
  };
}
